import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";

/**
 * A time field with its own picker, replacing the browser's native one.
 *
 * The native `<input type="time">` opens a popup styled by the browser, not by
 * the app, and it looks it. This shows the time as "7:00 AM" in a field that
 * matches the rest of the form, and opens a small panel with three columns —
 * hour, minute, AM/PM — where a tap on any value applies it at once. The
 * value in and out is "HH:mm", the shape the timesheet stores, so nothing
 * around it changes.
 *
 * Minutes are offered in fives, since that is how hours are recorded almost
 * every time; a value already on the field that is not a multiple of five is
 * kept in the list so it stays selectable and shown as chosen. Anything can
 * still be typed into the field — "7:07 AM", "07:07", "1907" all read.
 */
const PANEL_WIDTH = 236;
const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const parseTyped = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return null;
  for (const format of ["h:mm A", "h:mmA", "h A", "hA", "HH:mm", "H:mm", "HHmm", "Hmm"]) {
    const parsed = moment(trimmed.toUpperCase(), format, true);
    if (parsed.isValid()) return parsed.format("HH:mm");
  }
  return undefined;
};

const partsOf = (value) => {
  const parsed = value ? moment(value, "HH:mm", true) : null;
  if (!parsed || !parsed.isValid()) return { hour: null, minute: null, meridiem: null };
  return {
    hour: Number(parsed.format("h")),
    minute: parsed.minute(),
    meridiem: parsed.format("A"),
  };
};

const compose = ({ hour, minute, meridiem }) =>
  moment(`${hour ?? 12}:${String(minute ?? 0).padStart(2, "0")} ${meridiem ?? "AM"}`, "h:mm A").format(
    "HH:mm"
  );

const display = (value) => {
  const parsed = value ? moment(value, "HH:mm", true) : null;
  return parsed && parsed.isValid() ? parsed.format("h:mm A") : "";
};

// The chip on the panel title: green for a start, red for a stop, so the two
// panels read apart at a glance and not only by their wording.
const END_CHIP = {
  start: "bg-[#DCFCE7] text-[#166534] ring-[#86EFAC]",
  stop: "bg-[#FEE2E2] text-[#991B1B] ring-[#FCA5A5]",
};

const TimeField = ({ value, onChange, label, end, placeholder = "--:-- --", className = "" }) => {
  const endKey = typeof end === "string" ? end.toLowerCase() : null;
  const title = [label, end].filter(Boolean).join(" · ");
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(display(value));
  const [rect, setRect] = useState(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const columnRefs = useRef({});

  // The field follows the stored value whenever it changes — a pick in the
  // panel, a save elsewhere — so what the box says is always what the day
  // holds. Typing overrides it only until the typed text is committed.
  useEffect(() => {
    setText(display(value));
  }, [value]);

  const parts = useMemo(() => partsOf(value), [value]);

  // What the field held when the panel opened. Once a different value is
  // picked, the old one keeps a faint box so the person can see where they
  // came from and get back to it in one tap.
  const [openedWith, setOpenedWith] = useState({ hour: null, minute: null, meridiem: null });
  useEffect(() => {
    if (open) setOpenedWith(partsOf(value));
    // Captured on open only; the current value is deliberately not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const minutes = useMemo(() => {
    const list = [];
    for (let m = 0; m < 60; m += 5) list.push(m);
    for (const minute of [parts.minute, openedWith.minute]) {
      if (minute != null && !list.includes(minute)) list.push(minute);
    }
    list.sort((a, b) => a - b);
    return list;
  }, [parts.minute, openedWith.minute]);

  // Portaled and fixed, so the drawer or table the field sits in cannot clip
  // it — the same reason PayRulesDropdown portals its panel.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const ESTIMATED_HEIGHT = 250;
      const below = window.innerHeight - anchor.bottom;
      const flipUp = below < ESTIMATED_HEIGHT && anchor.top > below;
      setRect({
        top: flipUp ? null : Math.round(anchor.bottom + 6),
        bottom: flipUp ? Math.round(window.innerHeight - anchor.top + 6) : null,
        left: Math.round(Math.max(8, Math.min(anchor.left, window.innerWidth - PANEL_WIDTH - 8))),
      });
    };
    place();
    const onPointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Each column opens scrolled to its chosen value.
  useEffect(() => {
    if (!open) return;
    for (const key of ["hour", "minute"]) {
      const column = columnRefs.current[key];
      const chosen = column?.querySelector("[data-chosen='true']");
      if (column && chosen) column.scrollTop = chosen.offsetTop - column.clientHeight / 2 + chosen.clientHeight / 2;
    }
  }, [open]);

  const pick = (patch) => {
    const next = compose({ ...parts, ...patch });
    // Written into the box at once rather than waiting for the value to come
    // back round through the parent, so the pick is visible on the tap.
    setText(display(next));
    onChange(next);
  };

  const commitTyped = () => {
    const parsed = parseTyped(text);
    if (parsed === undefined) {
      // Not readable: put the stored value back rather than keep a field that
      // says one thing while the day holds another.
      setText(display(value));
      return;
    }
    if (parsed !== (value ?? null)) onChange(parsed);
    setText(display(parsed));
  };

  const columnClass = "flex flex-col overflow-y-auto max-h-[196px] py-1 [scrollbar-width:thin]";
  const itemClass = (chosen, was) =>
    `w-full px-2 py-[5px] rounded-[6px] text-center text-[13px] leading-[18px] tabular-nums ${
      chosen
        ? "bg-[#0058E5] text-white font-OpenSansSemiBold"
        : was
          ? "bg-[#EAF3FF] text-[#0058E5] font-OpenSansSemiBold ring-1 ring-inset ring-[#C7DBFF] hover:bg-[#DCE8FF]"
          : "text-[#1A2332] font-OpenSansRegular hover:bg-[#EAF3FF] hover:text-[#0058E5]"
    }`;

  return (
    <span ref={anchorRef} className={`relative inline-flex ${className}`}>
      <input
        value={text}
        placeholder={placeholder}
        onChange={(event) => setText(event.target.value)}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={commitTyped}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitTyped();
            setOpen(false);
          }
        }}
        aria-haspopup="dialog"
        aria-label={title}
        title={title}
        className={`h-[30px] w-[104px] pl-2 pr-7 rounded-[8px] border-[1px] bg-white font-OpenSansRegular text-[14px] text-titleColor outline-none placeholder:text-[#A0A6BA] ${
          open ? "border-[#0065FD] ring-2 ring-[#DCE8FF]" : "border-borderColor"
        }`}
      />
      <button
        type="button"
        tabIndex={-1}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((previous) => !previous)}
        aria-label="Pick a time"
        className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 rounded-[6px] flex items-center justify-center text-[#868DA6] hover:bg-[#F0F1F5] hover:text-[#0058E5]"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
          <path d="M8 4.5V8l2.4 1.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </button>

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              style={{ left: rect.left, top: rect.top ?? undefined, bottom: rect.bottom ?? undefined, width: PANEL_WIDTH }}
              className="fixed z-[70] rounded-[10px] border border-[#E5E8F3] bg-white p-2 shadow-[0px_12px_32px_rgba(16,24,40,0.16)]"
            >
              {/* Which time this is. Two identical panels a few inches apart,
                  one for the start and one for the stop, are otherwise told
                  apart only by remembering which box was clicked. */}
              {label || end ? (
                <p className="px-1 pb-2 mb-1 border-b border-[#F1F5F9] flex items-center gap-2 text-[12px] leading-4 font-OpenSansSemiBold text-[#1A2332]">
                  {label ? <span>{label}</span> : null}
                  {end ? (
                    <span
                      className={`px-2 py-[1px] rounded-[5px] ring-1 ring-inset text-[11px] leading-4 uppercase tracking-[0.5px] ${
                        END_CHIP[endKey] ?? "bg-[#F1F5F9] text-[#45556C] ring-[#CBD5E1]"
                      }`}
                    >
                      {end}
                    </span>
                  ) : null}
                </p>
              ) : null}
              <div className="grid grid-cols-[1fr_1fr_64px] gap-1">
                {["Hour", "Min", ""].map((label) => (
                  <p
                    key={label || "ampm"}
                    className="px-1 pb-1 text-[10px] leading-4 font-OpenSansSemiBold uppercase tracking-[0.5px] text-[#868DA6] text-center"
                  >
                    {label}
                  </p>
                ))}
                <div ref={(node) => (columnRefs.current.hour = node)} className={columnClass}>
                  {HOURS.map((hour) => (
                    <button
                      key={hour}
                      type="button"
                      data-chosen={parts.hour === hour}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pick({ hour })}
                      className={itemClass(parts.hour === hour, openedWith.hour === hour)}
                    >
                      {hour}
                    </button>
                  ))}
                </div>
                <div ref={(node) => (columnRefs.current.minute = node)} className={columnClass}>
                  {minutes.map((minute) => (
                    <button
                      key={minute}
                      type="button"
                      data-chosen={parts.minute === minute}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pick({ minute })}
                      className={itemClass(parts.minute === minute, openedWith.minute === minute)}
                    >
                      {String(minute).padStart(2, "0")}
                    </button>
                  ))}
                </div>
                <div className="flex flex-col gap-1 py-1">
                  {["AM", "PM"].map((meridiem) => (
                    <button
                      key={meridiem}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => pick({ meridiem })}
                      className={itemClass(parts.meridiem === meridiem, openedWith.meridiem === meridiem)}
                    >
                      {meridiem}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-[#F1F5F9] flex items-center justify-between">
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(null);
                    setText("");
                  }}
                  className="text-[12px] leading-[18px] font-OpenSansRegular text-[#868DA6] hover:text-[#1A2332]"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setOpen(false)}
                  className="h-7 px-3 rounded-[6px] bg-[#0058E5] text-white text-[12px] font-OpenSansSemiBold"
                >
                  Done
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

export default TimeField;
